// Elevation via Open-Meteo's free elevation API (no key, CORS-enabled).
// The public OSM Valhalla servers don't ship elevation tiles (return null),
// so we sample the route, fetch heights from Open-Meteo, then build
// colour-graded segments + ascent/descent totals.

import { haversineKm } from './geomath';

// Heights come from our own Cloud Function, which proxies Open-Meteo and caches
// results in Firestore keyed by the sampled coordinates — so each stretch of
// road is fetched from the upstream API once for ALL users, sidestepping its
// per-IP daily rate limit.
const ELEV_FN_URL = 'https://us-central1-roamr-4f8ad.cloudfunctions.net/getElevation';
const HEIGHT_TIMEOUT_MS = 12_000;
const REQ_CHUNK = 100;     // Open-Meteo / proxy limit per request
const MAX_SAMPLES = 400;   // total samples (batched) → high-res grade colouring
const MIN_STEP_M = 200;    // don't sample finer than the DEM can resolve

// ── Elevation cache ──────────────────────────────────────────────────────
// The elevation API is a free, rate-limited service (Open-Meteo caps daily
// requests per IP, then returns 429). Cache results by route signature so a
// route keeps its profile across reloads/toggles and we stop re-hitting the
// API for geometry we've already measured.
const ELEV_CACHE_KEY = 'roamr-elev-cache-v1';
const ELEV_CACHE_MAX = 24;

function readElevCache() {
  try { return JSON.parse(localStorage.getItem(ELEV_CACHE_KEY)) || {}; }
  catch { return {}; }
}

export function getCachedElevation(key) {
  return readElevCache()[key] || null;
}

export function cacheElevation(key, value) {
  if (!key || !value) return;
  try {
    const cache = readElevCache();
    delete cache[key];                 // move-to-end for crude LRU
    cache[key] = value;
    const keys = Object.keys(cache);
    while (keys.length > ELEV_CACHE_MAX) delete cache[keys.shift()];
    localStorage.setItem(ELEV_CACHE_KEY, JSON.stringify(cache));
  } catch { /* quota / disabled storage — ignore */ }
}

// Grade (%) → colour. Diverging scale: blue (down) → grey (flat) → red (climb).
export function gradeColor(grade) {
  if (grade <= -6) return '#2563eb'; // steep descent
  if (grade <= -2) return '#00c2a8'; // descent
  if (grade <   2) return '#9aa1ac'; // flat
  if (grade <   6) return '#ff7a1a'; // climb
  return '#f5564a';                  // steep climb
}

export const GRADE_LEGEND = [
  { color: '#2563eb', label: 'Steep ↓' },
  { color: '#00c2a8', label: 'Down'    },
  { color: '#9aa1ac', label: 'Flat'    },
  { color: '#ff7a1a', label: 'Up'      },
  { color: '#f5564a', label: 'Steep ↑' },
];

async function fetchHeightChunk(indices, coords, signal) {
  const lats = indices.map(i => Number(coords[i][1].toFixed(5)));
  const lngs = indices.map(i => Number(coords[i][0].toFixed(5)));
  const res = await fetch(ELEV_FN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lats, lngs }),
    signal,
  });
  if (!res.ok) throw new Error(`elevation ${res.status}`);
  const data = await res.json();
  if (!Array.isArray(data.heights)) throw new Error('no elevation');
  return data.heights;
}

async function fetchHeights(boundary, coords, signal) {
  // Split into ≤100-point requests (each cached independently by the proxy),
  // run in parallel, then concatenate in order.
  const chunks = [];
  for (let i = 0; i < boundary.length; i += REQ_CHUNK) chunks.push(boundary.slice(i, i + REQ_CHUNK));
  // The proxy needs ≥2 points per request — fold a trailing size-1 chunk back.
  if (chunks.length > 1 && chunks[chunks.length - 1].length === 1) {
    const last = chunks.pop();
    last.unshift(chunks[chunks.length - 1].pop());
    chunks.push(last);
  }
  const parts = await Promise.all(chunks.map(ch => fetchHeightChunk(ch, coords, signal)));
  return parts.flat();
}

/**
 * @param {number[][]} coords  GeoJSON [lng,lat] pairs from the route
 * @returns {Promise<null | {
 *   segments: { positions:[number,number][], grade:number, ele:number }[],
 *   minEle:number, maxEle:number, ascent:number, descent:number
 * }>}
 */
export async function fetchElevationSegments(coords, signal) {
  if (!coords || coords.length < 3) return null;

  const n = coords.length;

  // Distance-based sampling → roughly equal-length segments for clean, accurate
  // grade colouring. One sample per `stepM` (≥ MIN_STEP_M), capped at
  // MAX_SAMPLES so a long route still resolves finely without unbounded calls.
  const cumM = [0];
  for (let i = 1; i < n; i++) {
    cumM[i] = cumM[i - 1] + haversineKm(coords[i - 1][1], coords[i - 1][0], coords[i][1], coords[i][0]) * 1000;
  }
  const totalM = cumM[n - 1];
  const stepM = Math.max(MIN_STEP_M, totalM / MAX_SAMPLES);

  const boundary = [0];
  let nextAt = stepM;
  for (let i = 1; i < n; i++) {
    if (cumM[i] >= nextAt) { boundary.push(i); nextAt += stepM; }
  }
  if (boundary[boundary.length - 1] !== n - 1) boundary.push(n - 1);

  let heights;
  const ctl = new AbortController();
  const onAbort = () => ctl.abort();
  signal?.addEventListener('abort', onAbort);
  const timer = setTimeout(() => ctl.abort(), HEIGHT_TIMEOUT_MS);
  try {
    heights = await fetchHeights(boundary, coords, ctl.signal);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onAbort);
  }

  let minEle = Infinity, maxEle = -Infinity, ascent = 0, descent = 0;
  for (const h of heights) {
    if (h == null) continue;
    if (h < minEle) minEle = h;
    if (h > maxEle) maxEle = h;
  }

  const segments = [];
  for (let k = 0; k < boundary.length - 1; k++) {
    const a = boundary[k], b = boundary[k + 1];
    const positions = coords.slice(a, b + 1).map(([lon, lat]) => [lat, lon]);
    const eleA = heights[k], eleB = heights[k + 1];

    let grade = 0, ele = eleB ?? eleA ?? 0;
    if (eleA != null && eleB != null) {
      let horiz = 0;
      for (let j = a; j < b; j++) {
        horiz += haversineKm(coords[j][1], coords[j][0], coords[j + 1][1], coords[j + 1][0]) * 1000;
      }
      grade = horiz > 5 ? ((eleB - eleA) / horiz) * 100 : 0;
      const d = eleB - eleA;
      if (d > 0) ascent += d; else descent -= d;
    }
    if (positions.length >= 2) segments.push({ positions, grade, ele: Math.round(ele) });
  }

  if (!segments.length) return null;

  // Boundary points for fast nearest-point lookup on hover (tooltip values).
  const points = boundary.map((idx, k) => ({
    lat: coords[idx][1],
    lng: coords[idx][0],
    ele: Math.round(heights[k] ?? 0),
    grade: segments[Math.min(k, segments.length - 1)]?.grade ?? 0,
  }));

  return {
    segments,
    points,
    minEle: Math.round(minEle),
    maxEle: Math.round(maxEle),
    ascent: Math.round(ascent),
    descent: Math.round(descent),
  };
}
